import { useEffect, useState } from 'react'

import { Box, Section, Text } from '@factorialco/deprecated-design-system'
import { NumberInput } from '@factorialco/f0-react/dist/experimental'
import {
  SalaryAdvanceSettingsPageDocument,
  SaveSalaryAdvanceCompanySettingDocument,
  type SaveSalaryAdvanceCompanySettingMutation,
} from 'generated/resources/graphql'

import { SingleRangeSlider } from 'modules/core/components/SingleRangeSlider'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { fromCents, toCents } from 'modules/core/lib/formatCurrencyUtils'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import {
  MAX_CAP_PERCENTAGE,
  MIN_CAP_PERCENTAGE,
  normalizeCapPercentage,
} from 'modules/salary_advance/lib/companySettings'

type Props = {
  currentCapPercentage: number | null | undefined
  currentFixedAmountCents: number | null
  requestWindowStartDay: number
  requestDeadlineDay: number
  serviceEnabled: boolean
  locale: string
  currency: string
  canEdit: boolean
}

const normalizeFixedAmount = (fixedAmountCents: number | null) =>
  fixedAmountCents == null ? 0 : fromCents(fixedAmountCents)

const normalizeSelectedFixedAmount = (value: number | null | undefined) => {
  if (value == null || !Number.isFinite(value)) return 0

  return Math.max(value, 0)
}

const CapPercentageSetting = ({
  currentCapPercentage,
  currentFixedAmountCents,
  requestWindowStartDay,
  requestDeadlineDay,
  serviceEnabled,
  locale,
  currency,
  canEdit,
}: Props) => {
  const normalizedCapPercentage = normalizeCapPercentage(currentCapPercentage)
  const normalizedFixedAmount = normalizeFixedAmount(currentFixedAmountCents)
  const [selectedCapPercentage, setSelectedCapPercentage] = useState<number | null>(
    normalizedCapPercentage
  )
  const [hasEditedCapPercentage, setHasEditedCapPercentage] = useState(false)
  const [selectedFixedAmount, setSelectedFixedAmount] = useState<number>(normalizedFixedAmount)
  const [hasEditedFixedAmount, setHasEditedFixedAmount] = useState(false)

  const [saveCompanySetting, { loading }] = useMutation(SaveSalaryAdvanceCompanySettingDocument, {
    extractorFn: (payload: SaveSalaryAdvanceCompanySettingMutation) =>
      payload.salaryAdvanceMutations?.saveSalaryadvanceCompanySetting ?? { errors: [] },
    refetchQueries: [SalaryAdvanceSettingsPageDocument],
  })

  useEffect(() => {
    setSelectedCapPercentage(normalizeCapPercentage(currentCapPercentage))
    setHasEditedCapPercentage(false)
    setSelectedFixedAmount(normalizeFixedAmount(currentFixedAmountCents))
    setHasEditedFixedAmount(false)
  }, [currentCapPercentage, currentFixedAmountCents])

  const hasFixedAmount = Number.isFinite(selectedFixedAmount) && selectedFixedAmount > 0
  const selectedFixedAmountCents = hasFixedAmount ? toCents(selectedFixedAmount) : null
  const selectedFixedAmountCentsToSave = hasEditedFixedAmount
    ? selectedFixedAmountCents
    : currentFixedAmountCents
  const sliderValue = selectedCapPercentage ?? MIN_CAP_PERCENTAGE
  const selectedCapPercentageToSave = hasEditedCapPercentage
    ? sliderValue
    : (currentCapPercentage ?? null)
  const isDirty =
    selectedCapPercentageToSave !== currentCapPercentage ||
    selectedFixedAmountCentsToSave !== currentFixedAmountCents

  const handleSave = async () => {
    const result = await saveCompanySetting({
      variables: {
        advanceCapMonthlyWagePercent: selectedCapPercentageToSave,
        advanceCapFixedAmountCents: selectedFixedAmountCentsToSave,
        requestWindowStartDay,
        requestDeadlineDay,
        serviceEnabled,
      },
    })
    const handledResult = handleResult(result, () => {})

    track(trackingEvents.salaryAdvance.settingsSaveResult, {
      setting_key: 'advance_cap_monthly_wage_percent',
      setting_value_percentage: selectedCapPercentageToSave,
      setting_value_amount_cents: selectedFixedAmountCentsToSave,
      outcome: handledResult.success ? 'success' : 'failure',
    })

    if (handledResult.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_error'),
      })
    }
  }

  return (
    <Section.Form
      illustration="money-bag"
      title={i18n.t('salary_advance.settings.cap_percentage.new_title')}
      description={i18n.t('salary_advance.settings.cap_percentage.combined_description')}
      submitLabel={i18n.t('common.save')}
      isDirty={isDirty}
      onSubmit={handleSave}
      disabled={!canEdit || loading}
    >
      <Box flexDirection="column" gap="s24">
        <Box flexDirection="column" gap="s12">
          <Text.Normal weight="semibold" size="200">
            {selectedCapPercentage == null && !hasEditedCapPercentage
              ? i18n.t('salary_advance.settings.cap_percentage.percentage_input_label')
              : i18n.t('salary_advance.settings.cap_percentage.percentage_label', {
                  percentage: sliderValue,
                })}
          </Text.Normal>
          <SingleRangeSlider
            min={MIN_CAP_PERCENTAGE}
            max={MAX_CAP_PERCENTAGE}
            value={sliderValue}
            onChange={(value) => {
              setHasEditedCapPercentage(true)
              setSelectedCapPercentage(value)
            }}
            readonly={!canEdit}
          />
        </Box>
        <Box flexDirection="column" gap="s8">
          <Text.Normal weight="semibold" size="200">
            {i18n.t('salary_advance.settings.fixed_amount.input_label')}
          </Text.Normal>
          <NumberInput
            label={i18n.t('salary_advance.settings.fixed_amount.input_label')}
            hideLabel
            locale={locale}
            units={currency}
            value={selectedFixedAmount}
            step={0.01}
            maxDecimals={2}
            onChange={(value) => {
              setHasEditedFixedAmount(true)
              setSelectedFixedAmount(normalizeSelectedFixedAmount(value))
            }}
            size="md"
          />
          <Text.Normal size="100" color="grey700">
            {i18n.t('salary_advance.settings.fixed_amount.zero_disables_hint')}
          </Text.Normal>
        </Box>
      </Box>
    </Section.Form>
  )
}

export default CapPercentageSetting
